import { injectable } from 'inversify'

import { BaseState } from './BaseState'

export interface UserGeometryLayerVisibilityStateProps {
  // Maps a geometry id to the indices of its hidden layers (features), indexed
  // into the geometry's derived feature list (see buildLayerItems). Runtime-only
  // (Q6/T-D4): never persisted or serialized, so a reload restores every layer to
  // visible. An absent key means all layers are visible.
  hiddenByGeometry: Record<string, number[]>
}

const INITIAL_STATE: UserGeometryLayerVisibilityStateProps = {
  hiddenByGeometry: {}
}

// Runtime store for per-layer visibility of user geometries. The eye toggle in the
// layer list (T-C3) writes here; the map presenter reads it to drop hidden features
// at render time (after the .fgb cache lookup, never baking into the cached object).
@injectable()
export class UserGeometryLayerVisibilityState extends BaseState<UserGeometryLayerVisibilityStateProps> {
  constructor() {
    super(INITIAL_STATE)
  }

  isHidden(geometryId: string, layerIndex: number): boolean {
    return (this.getStateSnapshot().hiddenByGeometry[geometryId] ?? []).includes(layerIndex)
  }

  getHiddenIndices(geometryId: string): number[] {
    return this.getStateSnapshot().hiddenByGeometry[geometryId] ?? []
  }

  setHidden(geometryId: string, layerIndex: number, hidden: boolean): void {
    const current = this.getStateSnapshot().hiddenByGeometry[geometryId] ?? []
    const isCurrentlyHidden = current.includes(layerIndex)
    if (hidden === isCurrentlyHidden) return

    const nextForGeometry = hidden ? [...current, layerIndex] : current.filter((index) => index !== layerIndex)
    this._writeGeometry(geometryId, nextForGeometry)
  }

  toggle(geometryId: string, layerIndex: number): void {
    this.setHidden(geometryId, layerIndex, !this.isHidden(geometryId, layerIndex))
  }

  // Drops all visibility overrides for a geometry (e.g. when it is deleted or its
  // .fgb changes so the old indices no longer map to the same layers).
  clearGeometry(geometryId: string): void {
    const map = this.getStateSnapshot().hiddenByGeometry
    if (!(geometryId in map)) return
    const next = { ...map }
    delete next[geometryId]
    this._updateStateProp('hiddenByGeometry', next)
  }

  private _writeGeometry(geometryId: string, hiddenIndices: number[]): void {
    const next = { ...this.getStateSnapshot().hiddenByGeometry }
    if (hiddenIndices.length === 0) {
      delete next[geometryId]
    } else {
      next[geometryId] = hiddenIndices
    }
    this._updateStateProp('hiddenByGeometry', next)
  }
}
